import { useEffect } from "react"
import { useAuthStore } from "../../../stores/auth-store"
import { getLoginUrl } from "../../../lib/auth"

interface RequireAuthProps {
  children: React.ReactNode
}

/**
 * Wrapper component that protects routes from unauthenticated access.
 *
 * Behavior:
 * - Shows loading state while `isInitialized` is false (auth check in progress)
 * - Redirects to login page if user is not authenticated after init completes
 * - Renders children if user is authenticated
 *
 * Based on original app's LoggedInRoute pattern.
 */
export function RequireAuth({ children }: RequireAuthProps) {
  const isInitialized = useAuthStore((state) => state.isInitialized)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  useEffect(() => {
    // Only redirect after initialization is complete and user is not authenticated
    if (isInitialized && !isAuthenticated) {
      window.location.href = getLoginUrl()
    }
  }, [isInitialized, isAuthenticated])

  // Show loading while checking authentication
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  // If not authenticated, don't render anything (redirect is happening)
  if (!isAuthenticated) {
    return null
  }

  // User is authenticated, render children
  return <>{children}</>
}
